import React, { useState, useEffect } from 'react';
import { generalApi } from '../../api/generalApi';
import { referenceAPI } from '../../api/api';
import DataTable from '../../components/ui/DataTable';
import TableAction, { TableActionGroup } from '../../components/ui/TableAction';
import ActionToolbar from '../../components/ui/ActionToolbar';
import IconActionButton from '../../components/ui/IconActionButton';
import ResponsiveModal from '../../components/ui/ResponsiveModal';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';
import Loading from '../../components/ui/Loading';
import { PlusIcon, PencilSquareIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/outline';

const LicenseDefinitionsPage = () => {
  const [licenseTypes, setLicenseTypes] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    is_global: true,
    hotel: '',
  });
  const [saveLoading, setSaveLoading] = useState(false);

  const loadData = async () => {
    try {
      const [typesRes, hotelsRes] = await Promise.all([
        generalApi.getLicenseTypes(),
        referenceAPI.getHotels(),
      ]);
      const types = Array.isArray(typesRes) ? typesRes : (typesRes?.data || typesRes?.results || []);
      setLicenseTypes(types);
      const hotelsData = Array.isArray(hotelsRes) ? hotelsRes : (hotelsRes?.data || hotelsRes?.results || []);
      setHotels(hotelsData);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setFormData({
      name: '',
      code: '',
      description: '',
      is_global: true,
      hotel: '',
    });
    setModalOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setFormData({
      name: item.name || '',
      code: item.code || '',
      description: item.description || '',
      is_global: item.is_global ?? true,
      hotel: item.hotel || '',
    });
    setModalOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.name?.trim() || !formData.code?.trim()) {
      alert('Ad ve kod zorunludur.');
      return;
    }
    setSaveLoading(true);
    try {
      const payload = {
        name: formData.name.trim(),
        code: formData.code.trim().toUpperCase(),
        description: formData.description?.trim() || null,
        is_global: formData.is_global,
        hotel: formData.is_global ? null : (formData.hotel || null),
      };
      if (editing) {
        await generalApi.updateLicenseType(editing.id, payload);
      } else {
        await generalApi.createLicenseType(payload);
      }
      setModalOpen(false);
      loadData();
    } catch (err) {
      alert(err.message || 'Kayıt sırasında hata oluştu');
    } finally {
      setSaveLoading(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`"${item.name}" lisans tipini silmek istediğinize emin misiniz?`)) return;
    try {
      await generalApi.deleteLicenseType(item.id);
      loadData();
    } catch (err) {
      alert(err.message || 'Silme işlemi başarısız');
    }
  };

  const columns = [
    { header: 'Ad', accessorKey: 'name', size: 180 },
    { header: 'Kod', accessorKey: 'code', size: 100 },
    {
      header: 'Kapsam',
      accessorKey: 'is_global',
      size: 100,
      cell: ({ getValue }) => (getValue() ? 'Global' : 'Otel Bazlı'),
    },
    { header: 'Otel', accessorKey: 'hotel_name', size: 150, cell: ({ getValue }) => getValue() || '-' },
    {
      header: 'İşlemler',
      id: 'actions',
      size: 150,
      cell: ({ row }) => {
        const item = row.original;
        return (
          <TableActionGroup>
            <TableAction icon={PencilSquareIcon} onClick={() => openEdit(item)} color="blue" title="Düzenle" />
            <TableAction icon={TrashIcon} onClick={() => handleDelete(item)} color="rose" title="Sil" />
          </TableActionGroup>
        );
      },
    },
  ];

  if (loading) return <Loading fullScreen={false} message="Yükleniyor..." />;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Lisans Tipleri</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Lisans kategorilerini tanımlayın</p>
        </div>

        <ActionToolbar className="mb-4">
          <IconActionButton icon={PlusIcon} onClick={openCreate} label="Yeni Tip" />
        </ActionToolbar>

        <DataTable data={licenseTypes} columns={columns} />

        <ResponsiveModal isOpen={modalOpen} onClose={() => setModalOpen(false)} className="max-w-lg">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              {editing ? 'Lisans Tipi Düzenle' : 'Yeni Lisans Tipi'}
            </h2>
            <button type="button" onClick={() => setModalOpen(false)} className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300">
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
          <form onSubmit={handleSave} className="p-6 space-y-4">
            <div>
              <Label>Ad *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
                required
                placeholder="Örn: Yazılım Lisansı"
              />
            </div>
            <div>
              <Label>Kod *</Label>
              <Input
                value={formData.code}
                onChange={(e) => setFormData((p) => ({ ...p, code: e.target.value.toUpperCase() }))}
                required
                placeholder="Örn: SW"
                disabled={!!editing}
              />
            </div>
            <div>
              <Label>Açıklama</Label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData((p) => ({ ...p, description: e.target.value }))}
                rows={2}
                className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white"
              />
            </div>
            <div>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.is_global}
                  onChange={(e) => setFormData((p) => ({ ...p, is_global: e.target.checked }))}
                  className="rounded border-gray-300"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">Global (tüm oteller için)</span>
              </label>
            </div>
            {!formData.is_global && (
              <div>
                <Label>Otel</Label>
                <select
                  value={formData.hotel || ''}
                  onChange={(e) => setFormData((p) => ({ ...p, hotel: e.target.value || null }))}
                  className="w-full h-11 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 text-gray-900 dark:text-white"
                >
                  <option value="">Seçin</option>
                  {hotels.map((h) => (
                    <option key={h.id} value={h.id}>{h.hotel_name}</option>
                  ))}
                </select>
              </div>
            )}
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setModalOpen(false)}>
                İptal
              </Button>
              <Button type="submit" disabled={saveLoading}>
                {saveLoading ? 'Kaydediliyor...' : 'Kaydet'}
              </Button>
            </div>
          </form>
        </ResponsiveModal>
      </div>
    </div>
  );
};

export default LicenseDefinitionsPage;
